/**
 * Purchasing without internet (Phase 2 W5-4 the PC, W5-5 the phone; docs/plans/PHASE_2_W5.md §7):
 * the three sync commands, what a PC's page hands its main process, and the blind receipt task as a
 * device builds it from its own copy of the server's data.
 *
 * - `purchase.invoice.record` (PC): an invoice typed on the warehouse PC, its photos travelling as
 *   declarations (id, type, size, SHA-256) — the PC uploads exactly those bytes later (as waste, D276).
 * - `purchase.invoice.cancel` (PC): before the receipt only; after it → CONFLICT.
 * - `purchase.receipt.confirm` (PC, phone): the blind count, its damage photos declared the same way.
 *
 * The device's answer is only `{ invoiceId | receiptId, alreadyApplied }` (owner point 1: blind).
 * The receipt task carries no quantity, price, total or discount of the invoice — only the
 * supplier, the number and date, and each line's item and unit (plan §5.2, §9).
 */
import { z } from 'zod';

import { MAX_ATTACHMENT_BYTES } from '../attachments/attachments.ts';
import { PhotoHandoffRefSchema } from '../attachments/handoff.ts';
import { WastePhotoSchema } from '../inventory/stock.ts';
import type { StockUnit } from '../inventory/quantities.ts';
import type { StockCopy } from '../inventory/localStock.ts';
import { asReplicaSettingValue, effectiveSettingValue } from '../settings/effective.ts';
import {
  CANCEL_REASON_MIN,
  DUE_BEFORE_INVOICE,
  INVOICE_PHOTOS_MAX,
  PHOTO_REQUIRED,
  dueNotBeforeInvoice,
  invoiceContentFields,
  invoiceIdSchema,
} from './invoices.ts';
import {
  RECEIPT_ROWS_MAX,
  RECEIPT_ROW_PHOTOS_MAX,
  receiptIdSchema,
  receiptProblems,
  receiptRowFields,
} from './receipt.ts';

export const PURCHASE_INVOICE_RECORD_COMMAND = 'purchase.invoice.record';
export const PURCHASE_INVOICE_CANCEL_COMMAND = 'purchase.invoice.cancel';
export const PURCHASE_RECEIPT_CONFIRM_COMMAND = 'purchase.receipt.confirm';
export const RECEIPT_SECOND_PERSON_SETTING = 'purchase.receipt_second_person';

/** A photo a device declares in a command and uploads later: only these bytes are accepted. */
export const DevicePhotoSchema = WastePhotoSchema;
export type DevicePhoto = z.infer<typeof DevicePhotoSchema>;

const distinctPhotos = (photos: readonly { readonly id: string }[]) =>
  new Set(photos.map((p) => p.id)).size === photos.length;

// ── The commands (device → server) ───────────────────────────────────────────

export const PurchaseInvoiceCommandSchema = z
  .strictObject({
    id: invoiceIdSchema,
    /** The command's own location: a PC records invoices only where it stands (a warehouse). */
    locationId: z.uuid(),
    ...invoiceContentFields,
    photos: z
      .array(DevicePhotoSchema)
      .max(INVOICE_PHOTOS_MAX)
      .refine(distinctPhotos, { message: 'A photo appears twice' }),
    /**
     * The photos a phone sent through the QR code (W5-6; Q3: also while this PC had no internet).
     * Without its own photos, the invoice is refused when the server finds none for it.
     */
    photoHandoff: PhotoHandoffRefSchema.nullable().default(null),
  })
  .refine(dueNotBeforeInvoice, DUE_BEFORE_INVOICE)
  .refine((i) => i.photos.length > 0 || i.photoHandoff !== null, {
    ...PHOTO_REQUIRED,
    path: ['photos'],
  });
export type PurchaseInvoiceCommand = z.infer<typeof PurchaseInvoiceCommandSchema>;
export type PurchaseInvoiceCommandInput = z.input<typeof PurchaseInvoiceCommandSchema>;

export const PurchaseInvoiceCancelCommandSchema = z.strictObject({
  invoiceId: z.uuid(),
  reason: z.string().trim().min(CANCEL_REASON_MIN).max(500),
});
export type PurchaseInvoiceCancelCommand = z.infer<typeof PurchaseInvoiceCancelCommandSchema>;

export const GoodsReceiptCommandRowSchema = z.strictObject({
  ...receiptRowFields,
  /** DAMAGE photos of this row, declared (at least one when something is damaged, or a hand-off's). */
  damagePhotos: z.array(DevicePhotoSchema).max(RECEIPT_ROW_PHOTOS_MAX).default([]),
  /** The damage photos a phone sent for this row through the QR code (W5-6). */
  damageHandoff: PhotoHandoffRefSchema.nullable().default(null),
});

export const GoodsReceiptCommandSchema = z
  .strictObject({
    id: receiptIdSchema,
    invoiceId: z.uuid(),
    rows: z.array(GoodsReceiptCommandRowSchema).min(1).max(RECEIPT_ROWS_MAX),
    note: z.string().trim().max(500).nullable().default(null),
  })
  .refine((r) => distinctPhotos(r.rows.flatMap((row) => row.damagePhotos)), {
    message: 'A photo appears twice',
    path: ['rows'],
  });
export type GoodsReceiptCommand = z.infer<typeof GoodsReceiptCommandSchema>;
export type GoodsReceiptCommandInput = z.input<typeof GoodsReceiptCommandSchema>;

// ── What a PC's page hands its main process (photos as bytes) ─────────────────

const photoBytes = z
  .instanceof(Uint8Array)
  .refine((b) => b.byteLength >= 1 && b.byteLength <= MAX_ATTACHMENT_BYTES, {
    message: 'A photo is 1 byte to 10 MiB',
  });

/**
 * An invoice typed on a Dob Desktop PC (W5-4). The location is always the PC's own; the main
 * process makes the photos' declarations itself and saves the invoice and its photos in one
 * transaction of the PC's store.
 */
export const DesktopInvoiceInputSchema = z
  .strictObject({
    /** Chosen by the page, once per form: the same invoice sent again is saved once. */
    id: invoiceIdSchema,
    ...invoiceContentFields,
    photos: z.array(photoBytes).max(INVOICE_PHOTOS_MAX),
    /** The photos a phone sent through the QR code (W5-6). */
    photoHandoff: PhotoHandoffRefSchema.nullable().default(null),
  })
  .refine(dueNotBeforeInvoice, DUE_BEFORE_INVOICE)
  .refine((i) => i.photos.length > 0 || i.photoHandoff !== null, {
    ...PHOTO_REQUIRED,
    path: ['photos'],
  });
export type DesktopInvoiceInput = z.input<typeof DesktopInvoiceInputSchema>;

export const DesktopInvoiceCancelInputSchema = PurchaseInvoiceCancelCommandSchema;
export type DesktopInvoiceCancelInput = z.input<typeof DesktopInvoiceCancelInputSchema>;

/** A blind count typed on a Dob Desktop PC (W5-4): the damage photos as their bytes. */
export const DesktopReceiptInputSchema = z.strictObject({
  id: receiptIdSchema,
  invoiceId: z.uuid(),
  rows: z
    .array(
      z.strictObject({
        ...receiptRowFields,
        damagePhotos: z.array(photoBytes).max(RECEIPT_ROW_PHOTOS_MAX).default([]),
        damageHandoff: PhotoHandoffRefSchema.nullable().default(null),
      }),
    )
    .min(1)
    .max(RECEIPT_ROWS_MAX),
  note: z.string().trim().max(500).nullable().default(null),
});
export type DesktopReceiptInput = z.input<typeof DesktopReceiptInputSchema>;

// ── The blind receipt task (the same shapes as the API's, from a device's copy) ──

interface Named {
  readonly id: string;
  readonly nameAr: string;
  readonly nameEn: string;
}

/** An invoice waiting for its goods, as the receiver sees it: never a quantity or a price. */
export interface ReceiptTask {
  readonly invoiceId: string;
  readonly location: Named;
  readonly supplier: { readonly nameAr: string; readonly nameEn: string };
  readonly number: string;
  readonly invoiceDate: string;
  readonly lineCount: number;
  readonly recordedBySelf: boolean;
  readonly secondPersonRequired: boolean;
}

export interface ReceiptTaskLine {
  readonly lineNo: number;
  readonly item: Named & {
    readonly code: string;
    readonly stockUnit: StockUnit;
    readonly tracksBatches: boolean;
  };
  readonly unit: (Named & { readonly factor: number }) | null;
}

export interface ReceiptTaskDetail {
  readonly invoiceId: string;
  readonly status: 'WAITING' | 'CLOSED';
  readonly location: Named;
  readonly supplier: { readonly nameAr: string; readonly nameEn: string };
  readonly number: string;
  readonly invoiceDate: string;
  readonly recordedBySelf: boolean;
  readonly secondPersonRequired: boolean;
  readonly lines: readonly ReceiptTaskLine[];
}

/**
 * An invoice typed on this PC and not on the server yet: received here, its receipt is sent after
 * it (`dependsOn`, the same device; plan §5.1). Built by the PC from its own outbox.
 */
export interface PendingInvoiceTask {
  readonly invoiceId: string;
  readonly commandId: string;
  readonly locationId: string;
  readonly supplierId: string;
  readonly number: string;
  readonly invoiceDate: string;
  readonly recordedBy: string;
  readonly lines: readonly { readonly itemId: string; readonly unitId: string | null }[];
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function named(row: Record<string, unknown> | undefined, id: string): Named {
  return {
    id,
    nameAr: str(row?.['name_ar']),
    nameEn: str(row?.['name_en']) || str(row?.['name_ar']),
  };
}

/** Whether a count here needs a person other than the invoice's recorder (setting, default on). */
export function secondPersonRequired(copy: StockCopy, locationId: string, at: Date): boolean {
  const rows = copy
    .list('setting_value')
    .map((r) => asReplicaSettingValue(r))
    .filter((r) => r !== null);
  const value = effectiveSettingValue(rows, RECEIPT_SECOND_PERSON_SETTING, locationId, at);
  if (typeof value === 'boolean') return value;
  const definition = copy
    .list('setting_definition')
    .find((d) => d['key'] === RECEIPT_SECOND_PERSON_SETTING);
  const fallback = definition?.['default_value'];
  return typeof fallback === 'boolean' ? fallback : true;
}

interface TaskSource {
  readonly invoiceId: string;
  readonly locationId: string;
  readonly supplier: { readonly nameAr: string; readonly nameEn: string };
  readonly number: string;
  readonly invoiceDate: string;
  readonly recordedBy: string;
  readonly lines: readonly { lineNo: number; itemId: string; unitId: string | null }[];
}

/** The tasks of a location: the server's open tasks in the copy, then this PC's own pending ones. */
function taskSources(
  copy: StockCopy,
  locationId: string,
  pending: readonly PendingInvoiceTask[],
): TaskSource[] {
  const linesByInvoice = new Map<
    string,
    { lineNo: number; itemId: string; unitId: string | null }[]
  >();
  for (const l of copy.list('purchase_receipt_task_line')) {
    const invoiceId = str(l['invoice_id']);
    const list = linesByInvoice.get(invoiceId) ?? [];
    list.push({
      lineNo: Number(l['line_no']),
      itemId: str(l['item_id']),
      unitId: strOrNull(l['unit_id']),
    });
    linesByInvoice.set(invoiceId, list);
  }
  const fromServer: TaskSource[] = copy
    .list('purchase_receipt_task')
    .filter((t) => str(t['location_id']) === locationId)
    .map((t) => ({
      invoiceId: str(t['id']),
      locationId,
      supplier: {
        nameAr: str(t['supplier_name_ar']),
        nameEn: str(t['supplier_name_en']) || str(t['supplier_name_ar']),
      },
      number: str(t['number']),
      invoiceDate: str(t['invoice_date']),
      recordedBy: str(t['recorded_by']),
      lines: (linesByInvoice.get(str(t['id'])) ?? []).sort((a, b) => a.lineNo - b.lineNo),
    }));
  const known = new Set(fromServer.map((t) => t.invoiceId));
  const suppliers = new Map(copy.list('supplier').map((s) => [str(s['id']), s]));
  const own: TaskSource[] = pending
    .filter((p) => p.locationId === locationId && !known.has(p.invoiceId))
    .map((p) => {
      const s = suppliers.get(p.supplierId);
      return {
        invoiceId: p.invoiceId,
        locationId,
        supplier: {
          nameAr: str(s?.['name_ar']),
          nameEn: str(s?.['name_en']) || str(s?.['name_ar']),
        },
        number: p.number,
        invoiceDate: p.invoiceDate,
        recordedBy: p.recordedBy,
        lines: p.lines.map((l, i) => ({ lineNo: i + 1, itemId: l.itemId, unitId: l.unitId })),
      };
    });
  return [...fromServer, ...own].sort(
    (a, b) => a.invoiceDate.localeCompare(b.invoiceDate) || a.invoiceId.localeCompare(b.invoiceId),
  );
}

/** The invoices waiting for their goods at this PC's location, blind (the API's list shape). */
export function receiptTasksFromCopy(
  copy: StockCopy,
  locationId: string,
  userId: string,
  pending: readonly PendingInvoiceTask[],
  at: Date,
): ReceiptTask[] {
  const location = named(
    copy.list('location').find((l) => str(l['id']) === locationId),
    locationId,
  );
  const secondPerson = secondPersonRequired(copy, locationId, at);
  return taskSources(copy, locationId, pending).map((t) => ({
    invoiceId: t.invoiceId,
    location,
    supplier: t.supplier,
    number: t.number,
    invoiceDate: t.invoiceDate,
    lineCount: t.lines.length,
    recordedBySelf: t.recordedBy === userId,
    secondPersonRequired: secondPerson,
  }));
}

/** One task, blind (the API's detail shape): each line's item and unit, nothing of the invoice's amounts. */
export function receiptTaskFromCopy(
  copy: StockCopy,
  locationId: string,
  userId: string,
  pending: readonly PendingInvoiceTask[],
  invoiceId: string,
  at: Date,
): ReceiptTaskDetail | null {
  const task = taskSources(copy, locationId, pending).find((t) => t.invoiceId === invoiceId);
  if (!task) return null;
  const items = new Map(copy.list('item').map((i) => [str(i['id']), i]));
  const units = new Map(copy.list('item_unit').map((u) => [str(u['id']), u]));
  const location = named(
    copy.list('location').find((l) => str(l['id']) === locationId),
    locationId,
  );
  return {
    invoiceId: task.invoiceId,
    status: 'WAITING',
    location,
    supplier: task.supplier,
    number: task.number,
    invoiceDate: task.invoiceDate,
    recordedBySelf: task.recordedBy === userId,
    secondPersonRequired: secondPersonRequired(copy, locationId, at),
    lines: task.lines.map((l) => {
      const item = items.get(l.itemId);
      const unit = l.unitId ? units.get(l.unitId) : undefined;
      return {
        lineNo: l.lineNo,
        item: {
          ...named(item, l.itemId),
          code: str(item?.['code']),
          stockUnit: (str(item?.['stock_unit']) || 'PC') as StockUnit,
          tracksBatches: item?.['tracks_batches'] === true,
        },
        unit:
          l.unitId && unit ? { ...named(unit, l.unitId), factor: Number(unit['factor']) } : null,
      };
    }),
  };
}

// ── A blind count checked by the device before it saves (PC and phone) ────────

/** A counted row as a device holds it before saving (its photos counted, not read). */
export interface ReceiptCountRow {
  readonly invoiceLineNo: number | null;
  readonly itemId: string;
  readonly received: number;
  readonly damaged: number;
  readonly batchCode: string | null;
  readonly productionDate: string | null;
  readonly expiryDate: string | null;
  readonly damagePhotos: readonly unknown[];
  /** The damage photos a phone sent through the QR code (W5-6): they count as this row's. */
  readonly damageHandoff?: unknown;
}

/** A field path of the count (`rows.N.received` …, the server's own) and the server's code. */
export interface ReceiptCountProblem {
  readonly path: string;
  readonly code: string;
}

/**
 * The server's checks of a blind count (receipts.ts), made by Dob Desktop and Dob Staff before they
 * save it: every invoice line answered (0 is an answer), each row's item that of its line, damaged
 * not more than received, a batch item with an expiry date when something of it arrived and its
 * production date not after it, no batch fields on other items, and a damage photo when something
 * is damaged. No invoice quantity is needed or used: the count stays blind (owner point 1).
 */
export function receiptCountProblems(
  task: Pick<ReceiptTaskDetail, 'lines'>,
  rows: readonly ReceiptCountRow[],
  itemOf: (itemId: string) => { readonly tracksBatches: boolean } | undefined,
): ReceiptCountProblem[] {
  const problems: ReceiptCountProblem[] = [];
  const lines = task.lines.map((l) => ({
    lineNo: l.lineNo,
    itemId: l.item.id,
    quantity: 0,
    netTotal: 0,
  }));
  const counted = rows.map((r, i) => ({
    rowNo: i + 1,
    invoiceLineNo: r.invoiceLineNo,
    itemId: r.itemId,
    expiryDate: r.expiryDate,
    received: r.received,
    damaged: r.damaged,
  }));
  for (const p of receiptProblems(lines, counted)) {
    if (p.kind === 'line_missing') problems.push({ path: 'rows', code: 'line_missing' });
    else {
      const path = `rows.${String(p.rowNo - 1)}`;
      problems.push(
        p.kind === 'damaged_more_than_received'
          ? { path: `${path}.damaged`, code: p.kind }
          : p.kind === 'negative'
            ? { path: `${path}.received`, code: 'invalid' }
            : { path: `${path}.itemId`, code: p.kind },
      );
    }
  }
  rows.forEach((r, i) => {
    const path = `rows.${String(i)}`;
    const item = itemOf(r.itemId);
    if (!item) {
      problems.push({ path: `${path}.itemId`, code: 'unknown_item' });
      return;
    }
    if (item.tracksBatches) {
      if (r.received > 0 && r.expiryDate === null)
        problems.push({ path: `${path}.expiryDate`, code: 'expiry_required' });
      if (r.productionDate !== null && r.expiryDate !== null && r.productionDate > r.expiryDate)
        problems.push({ path: `${path}.productionDate`, code: 'dates_order' });
    } else if (r.batchCode !== null || r.expiryDate !== null || r.productionDate !== null) {
      problems.push({ path: `${path}.batchCode`, code: 'batch_not_used' });
    }
    if (r.damaged > 0 && r.damagePhotos.length === 0 && !r.damageHandoff)
      problems.push({ path: `${path}.damagePhotos`, code: 'damage_photo_required' });
  });
  return problems;
}
